/**
 * Reconstitution des poussees a partir d'evenements ponctuels.
 *
 * Tout ce que l'utilisateur saisit — une sensation ressentie, un passage aux
 * selles — est un evenement **instantane**. Le moteur de correlation, lui,
 * raisonne sur des episodes ayant un debut et une fin. Ce module fait le pont.
 *
 * ## Pourquoi des evenements ponctuels a la saisie
 *
 * Le modele a duree qui precedait demandait de cloturer chaque episode. Sur un
 * telephone, cet oubli est la regle plutot que l'exception — et sa consequence
 * etait severe : une fin non renseignee valant « toujours en cours », le moteur
 * traitait la fin comme infinie et **excluait de l'analyse tous les repas
 * suivants** pour ce symptome. L'analyse s'eteignait sans le moindre signe.
 *
 * ## Pourquoi regrouper malgre tout
 *
 * Une poussee produit plusieurs evenements rapproches : six passages aux selles
 * en deux jours, ou trois notes de douleur dans la journee. Comptes un par un,
 * ils feraient croire a autant d'« episodes distincts » — exactement le biais
 * que ce compteur sert a detecter. Les evenements de meme nature separes de
 * moins de `ecartMaxH` sont donc fusionnes en une seule poussee.
 *
 * ## Duree deduite
 *
 * Un evenement isole donne un episode de duree nulle : il ne recouvre aucun
 * repas et n'en censure aucun, ce qui est correct pour un fait ponctuel. Une
 * poussee reelle, elle, s'etale du premier au dernier evenement du groupe et
 * censure les repas qu'elle recouvre, comme le ferait un etat prolonge.
 */

import { HEURE_MS, epoch, toInstant } from '../domain/time'
import {
  BRISTOL_DUR_MAX,
  BRISTOL_MOU_MIN,
  DOULEUR_DEFECATION_MIN,
  type SelleDeriveType,
  type Sensation,
  type Stool,
  type SymptomEpisode,
  type SymptomType,
} from '../domain/types'

/** Ecart maximal entre deux evenements d'une meme poussee. */
export const ECART_REGROUPEMENT_H = 24

interface EvenementBrut {
  t: number
  intensite: number
}

/** Regroupe des instants tries en poussees, pour un type donne. */
export function grouperEnEpisodes(
  evenements: readonly EvenementBrut[],
  type: SymptomType,
  ecartMaxH = ECART_REGROUPEMENT_H,
): SymptomEpisode[] {
  if (evenements.length === 0) return []
  const ecart = ecartMaxH * HEURE_MS
  const tries = [...evenements].sort((a, b) => a.t - b.t)

  const episodes: SymptomEpisode[] = []
  let debut = tries[0]!.t
  let fin = debut
  let pic = tries[0]!.intensite

  const clore = () => {
    const iso = toInstant(debut)
    episodes.push({
      id: `derive-${type}-${debut}`,
      type,
      debutAt: iso,
      finAt: toInstant(fin),
      intensite: pic,
      creeLe: iso,
      modifieLe: iso,
    })
  }

  for (let i = 1; i < tries.length; i++) {
    const e = tries[i]!
    if (e.t - fin <= ecart) {
      fin = e.t
      if (e.intensite > pic) pic = e.intensite
    } else {
      clore()
      debut = e.t
      fin = e.t
      pic = e.intensite
    }
  }
  clore()

  return episodes
}

/** Natures analysables portees par un passage donne (il peut en cumuler plusieurs). */
export function naturesDe(s: Stool): SelleDeriveType[] {
  const out: SelleDeriveType[] = []
  if (s.bristol >= BRISTOL_MOU_MIN) out.push('selles-molles')
  if (s.bristol <= BRISTOL_DUR_MAX) out.push('selles-dures')
  if (s.urgence) out.push('urgence')
  if (s.evacuationIncomplete) out.push('evacuation-incomplete')
  if ((s.douleur ?? 0) >= DOULEUR_DEFECATION_MIN) out.push('douleur-defecation')
  return out
}

/**
 * Traduit sensations et selles en episodes exploitables par le moteur.
 *
 * C'est la seule fonction que l'interface doit appeler avant `analyser()`.
 */
export function episodesDerives(
  sensations: readonly Sensation[],
  selles: readonly Stool[],
  ecartMaxH = ECART_REGROUPEMENT_H,
): SymptomEpisode[] {
  const parType = new Map<SymptomType, EvenementBrut[]>()

  const ajouter = (type: SymptomType, t: number, intensite: number) => {
    if (!Number.isFinite(t)) return
    const l = parType.get(type)
    if (l) l.push({ t, intensite })
    else parType.set(type, [{ t, intensite }])
  }

  for (const s of sensations) ajouter(s.type, epoch(s.at), s.intensite)

  for (const s of selles) {
    const t = epoch(s.at)
    for (const nature of naturesDe(s)) {
      // L'intensite d'une selle n'a de sens que pour la douleur ; ailleurs, la
      // nature est deja portee par le type et une valeur neutre suffit.
      ajouter(nature, t, nature === 'douleur-defecation' ? (s.douleur ?? 5) : 5)
    }
  }

  const out: SymptomEpisode[] = []
  for (const [type, evts] of parType) out.push(...grouperEnEpisodes(evts, type, ecartMaxH))
  return out.sort((a, b) => a.debutAt.localeCompare(b.debutAt))
}

// ---------------------------------------------------------------------------
// Indicateurs de transit
// ---------------------------------------------------------------------------

export interface StatsSelles {
  total: number
  /** Nombre moyen de passages par jour suivi. */
  parJour: number
  /** Repartition par niveau de Bristol (index 0 = Bristol 1). */
  parBristol: number[]
  partUrgence: number
  partMolles: number
  partDures: number
  partIncompletes: number
  /** Jours comptant au moins un passage. */
  joursAvecPassage: number
}

export function statsSelles(selles: readonly Stool[], nbJoursSuivis: number): StatsSelles {
  const parBristol = [0, 0, 0, 0, 0, 0, 0]
  const jours = new Set<string>()
  let urgences = 0
  let molles = 0
  let dures = 0
  let incompletes = 0

  for (const s of selles) {
    const idx = s.bristol - 1
    if (idx >= 0 && idx < 7) parBristol[idx] = (parBristol[idx] ?? 0) + 1
    jours.add(s.at.slice(0, 10))
    if (s.urgence) urgences++
    if (s.bristol >= BRISTOL_MOU_MIN) molles++
    if (s.bristol <= BRISTOL_DUR_MAX) dures++
    if (s.evacuationIncomplete) incompletes++
  }

  const total = selles.length
  const part = (n: number) => (total > 0 ? n / total : 0)
  return {
    total,
    parJour: nbJoursSuivis > 0 ? total / nbJoursSuivis : 0,
    parBristol,
    partUrgence: part(urgences),
    partMolles: part(molles),
    partDures: part(dures),
    partIncompletes: part(incompletes),
    joursAvecPassage: jours.size,
  }
}
